// import { Injectable } from '@angular/core';
// import { MsalService } from '@azure/msal-angular';
// import { StoreSignupService } from '@services/cleanfill-core/registration/store-signup.service';
// import { StoreGroupsService } from '@services/cleanfill-core/store-groups/store-groups.service';
// import { StorePoliciesService } from '@services/cleanfill-core/store-policies/store-policies.service';
// import { StoreUsersService } from '@services/cleanfill-core/store-users/store-users.service';
// import { UserProfile } from '@view-models/login/user-profile';
// import { Account } from 'msal';
// import { NgxPermissionsService, NgxRolesService } from 'ngx-permissions';
// import { BehaviorSubject } from 'rxjs';
// import { filter, take } from 'rxjs/operators';
// import { ExpiredTokenHandler } from './expired-token-handler';

// @Injectable({ providedIn: 'root' })
// export class AuthService {
//   profileSubject$ = new BehaviorSubject<UserProfile>(null);
//   profile$ = this.profileSubject$.asObservable();

//   constructor(
//     private permissionsService: NgxPermissionsService,
//     private rolesService: NgxRolesService,
//     private userStore: StoreUsersService,
//     private msalService: MsalService,
//     private policiesStore: StorePoliciesService,
//     private groupsStore: StoreGroupsService,
//     private expiredTokenHandler: ExpiredTokenHandler,
//     private signupService: StoreSignupService
//   ) {
//     this.expiredTokenHandler.initialize();
//   }
//   login() {
//     // attempt to login
//     const account = this.msalService.getAccount();
//     if (account === null) {
//       return;
//     }
//     this.profileSubject$.next(this.getUserProfile(account));
//     this.userStore.loadActiveUser();
//     this.setRolesAndPermissions().then(t => {
//       this.policiesStore.loadPolicies();
//       this.groupsStore.loadGroups();
//       this.signupService.isPreviousLogin = true;
//     });
//     return Promise.resolve();
//   }

//   logout() {
//     this.msalService.logout();
//   }

//   async setRolesAndPermissions(): Promise<void> {
//     const [permissions, roles] = await Promise.all([
//       this.userStore.activeUserPermissions$
//         .pipe(
//           filter(p => p?.length > 0),
//           take(1)
//         )
//         .toPromise(),
//       this.userStore.activeUserRoles$
//         .pipe(
//           filter(p => p?.length > 0),
//           take(1)
//         )
//         .toPromise()
//     ]);

//     this.permissionsService.loadPermissions(permissions as string[]);
//     (roles as string[]).forEach(role => this.rolesService.addRole(role, permissions as string[]));
//   }

//   handleAuthRedirect() {
//     this.msalService.handleRedirectCallback((authError, response) => {
//       if (authError) {
//         console.error('Redirect Error: ', authError.errorMessage);
//         return;
//       }
//       console.log(response);
//       console.log('Redirect Success: ', response.accessToken);
//     });
//   }
//   getUserProfile(account: Account) {
//     const profile = new UserProfile();
//     profile.firstName = account?.idToken?.given_name ?? '';
//     profile.lastName = account?.idToken?.family_name ?? '';
//     profile.fullName = `${profile.firstName} ${profile.lastName}`.trim();
//     return profile;
//   }
// }
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { StoreGroupsService } from '@services/cleanfill-core/store-groups/store-groups.service';
import { StorePoliciesService } from '@services/cleanfill-core/store-policies/store-policies.service';
import { UserProfile } from '@view-models/login/user-profile';
import { NgxPermissionsService, NgxRolesService } from 'ngx-permissions';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import { ICurrentUserViewModel, LegalEntityStatus, UsersClient } from 'src/app/shared/services/cleanfill-api/cleanfill-api.generated';
@Injectable({ providedIn: 'root' })
export class AuthService {
  profileSubject$ = new BehaviorSubject<UserProfile>(null);
  profile$ = this.profileSubject$.asObservable();
  permissionsLoadedSubject$ = new BehaviorSubject<boolean>(false);
  permissionsLoaded$ = this.permissionsLoadedSubject$.asObservable().pipe(
    filter(t => t === true),
    distinctUntilChanged()
  );
  activeUserSubject$ = new BehaviorSubject<ICurrentUserViewModel>(null);
  activeUser$ = this.activeUserSubject$.asObservable().pipe(filter(t => t != null));
  activeUserId$ = this.activeUser$.pipe(map(t => t?.uniqueId));
  activeUserPermissions$ = this.activeUser$.pipe(map(user => user?.policies));
  activeUserRoles$ = this.activeUser$.pipe(map(user => user?.roles || []));
  activeUserLegalEntityType$ = this.activeUser$.pipe(map(user => user?.legalEntityType));
  activeUserLegalEntityStatus$ = this.activeUser$.pipe(map(user => user?.legalEntityStatus));
  activeUserLegalEntityUniqueId$ = this.activeUser$.pipe(map(user => user?.legalEntityUniqueId));
  isLoggedIn$ = combineLatest([this.activeUserLegalEntityUniqueId$, this.activeUserLegalEntityStatus$]).pipe(
    map(([id, status]) => id && status !== LegalEntityStatus.Draft)
  );

  get isPermissionsLoaded(): boolean {
    return this.permissionsLoadedSubject$.value;
  }

  get isLoggedIn(): boolean {
    return this.msalService.getAccount() !== null;
  }

  get activeUser() {
    return this.activeUserSubject$.value;
  }

  constructor(
    private userClient: UsersClient,
    private msalService: MsalService,
    private router: Router,
    private permissionsService: NgxPermissionsService,
    private rolesService: NgxRolesService,
    private policiesStore: StorePoliciesService,
    private groupsStore: StoreGroupsService
  ) { }

  loadPermissions(): Promise<ICurrentUserViewModel> {
    const account = this.msalService.getAccount();
    if (account) {
      return this.userClient
        .getCurrentUserDetails(undefined)
        .toPromise()
        .then(t => {
          if (t) {
            this.profileSubject$.next(this.getUserProfile(t));
            this.loadActiveUser(t);
            this.setRolesAndPermissions().then(x => {
              this.policiesStore.loadPolicies();
              this.groupsStore.loadGroups();
              if (x.length > 0) {
                this.permissionsLoadedSubject$.next(true);
              }
            });
          }
          return t;
        });
    }
    return of(null).toPromise();
  }

  loadActiveUser(user: ICurrentUserViewModel): boolean {
    this.permissionsLoadedSubject$.next((user?.policies?.length || 0) > 0);
    this.activeUserSubject$.next(user);
    return true;
  }
  patchActiveUserAcceptTerm(): boolean {
    this.activeUserSubject$.next({ ...this.activeUser, isTermAccepted: true });
    return true;
  }

  handleAuthRedirect(): void {
    this.msalService.handleRedirectCallback(
      t => {
        alert('handle redirect');
      },
      err => { }
    );
  }

  logout() {
    this.msalService.logout();
  }

  async setRolesAndPermissions(): Promise<string[]> {
    const [permissions, roles] = await Promise.all([
      this.activeUserPermissions$
        .pipe(
          filter(p => p?.length > 0),
          take(1)
        )
        .toPromise(),
      this.activeUserRoles$
        .pipe(
          filter(p => p?.length > 0),
          take(1)
        )
        .toPromise()
    ]);

    this.permissionsService.loadPermissions(permissions as string[]);
    (roles as string[]).forEach(role => this.rolesService.addRole(role, permissions as string[]));
    return permissions;
  }

  getUserProfile(account) {
    const profile = new UserProfile();
    profile.firstName = account?.firstName ?? '';
    profile.lastName = account?.lastName ?? '';
    profile.fullName = `${profile.firstName} ${profile.lastName}`.trim();
    return profile;
  }
}
